/**
 * Phase 8 (applicant-web slice) — case-state badge constants.
 *
 * Mirrors admin-web's case-state vocabulary so the applicant-side
 * status badge looks identical to the case team's view. Admin-web's
 * `caseStateBadgeColor` returns abstract color names ('gray',
 * 'indigo', etc.); this module pins those names to concrete hex
 * tokens drawn from the standard 600-weight palette so the
 * applicant-web can render the badge without pulling in admin-web's
 * private modules. The 7 admin states map 1:1 with the same color
 * names emitted by `caseStateBadgeColor`.
 *
 * A follow-up can promote this to a shared package and let admin-web
 * import these constants too — the applicant-web is the first surface
 * that needs the hex codes; admin-web only needs the color names.
 *
 * The seven colors are intentionally a small palette — each maps to
 * one of the seven case states. Any new state would need a paired
 * decision in BOTH admin-web's `BADGE_COLORS` and here.
 */

import type { TCaseState } from './caseState';

/**
 * Background / border / text triplet for a single state badge. Hex
 * values pulled from the Tailwind 600-weight palette so they sit
 * comfortably alongside the existing info-blue tokens
 * (`--color-info-bg` etc.) — the badge has its own bg+text so it
 * reads as a chip rather than a banner.
 */
export interface ICaseStateBadgeTokens {
  /** Light tint shown behind the badge label. */
  background: string;
  /** Outline that gives the chip its definition. */
  border: string;
  /** Foreground color used for the state label. */
  text: string;
  /** Stable color name shared with admin-web (for data attributes). */
  colorName: string;
}

/**
 * Per-state hex tokens. Order mirrors `CASE_STATES`.
 *
 * Color choices match admin-web's `caseStateBadgeColor` output:
 *   paid          -> gray   (#4b5563 / #f3f4f6)
 *   filling       -> blue   (#1d4ed8 / #dbeafe)
 *   submitted     -> indigo (#4338ca / #e0e7ff)
 *   in_review     -> amber  (#b45309 / #fef3c7)
 *   docket_ready  -> teal   (#0f766e / #ccfbf1)
 *   printing      -> purple (#6d28d9 / #ede9fe)
 *   shipped       -> green  (#15803d / #dcfce7)
 */
const BADGE_TOKENS: Record<TCaseState, ICaseStateBadgeTokens> = {
  paid: {
    background: '#f3f4f6',
    border: '#e5e7eb',
    text: '#4b5563',
    colorName: 'gray',
  },
  filling: {
    background: '#dbeafe',
    border: '#bfdbfe',
    text: '#1d4ed8',
    colorName: 'blue',
  },
  submitted: {
    background: '#e0e7ff',
    border: '#c7d2fe',
    text: '#4338ca',
    colorName: 'indigo',
  },
  in_review: {
    background: '#fef3c7',
    border: '#fde68a',
    text: '#b45309',
    colorName: 'amber',
  },
  docket_ready: {
    background: '#ccfbf1',
    border: '#99f6e4',
    text: '#0f766e',
    colorName: 'teal',
  },
  printing: {
    background: '#ede9fe',
    border: '#ddd6fe',
    text: '#6d28d9',
    colorName: 'purple',
  },
  shipped: {
    background: '#dcfce7',
    border: '#bbf7d0',
    text: '#15803d',
    colorName: 'green',
  },
};

export function getCaseStateBadgeTokens(
  state: TCaseState,
): ICaseStateBadgeTokens {
  return BADGE_TOKENS[state];
}
